import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { OutsideClickHandler } from '../../../components';

import { getISODateString, getStartOfDay, isValidDateString } from './DatePicker.helpers';
import DatePicker from './DatePicker';

import css from './SingleDatepicker.module.css';
import moment from 'moment';

const dateFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export const SingleDatePicker = props => {
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dateData, setDateData] = useState({
    date: props.value || null,
    formatted: props.value ? intl.formatDate(props.value, dateFormatOptions) : '',
  });
  const element = useRef(null);
  // Whether the latest interaction with the picker was a touch (vs. mouse/keyboard)
  const lastInteractionWasTouch = useRef(false);

  const {
    className,
    rootClassName,
    inputClassName,
    popupClassName,
    id,
    name,
    placeholderText,
    isDayBlocked,
    onChange,
    value,
    readOnly,
    ...rest
  } = props;

  // If value has changed, update internal state
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && value?.getTime() !== dateData?.date?.getTime()) {
      // If mounted, changes to value should be reflected to 'date' state
      setDateData({
        date: value,
        formatted: value ? intl.formatDate(value, dateFormatOptions) : '',
      });
    }
  }, [mounted, value]);

  const pickerId = `${id}_SingleDatePicker`;

  const classes = classNames(rootClassName || css.root, className, css.outsideClickWrapper);
  const startDateMaybe =
    dateData.date instanceof Date && !isNaN(dateData.date)
      ? { startDate: getISODateString(dateData.date) }
      : {};

  const handleChange = value => {
    const startOfDay = getStartOfDay(value);
    setDateData({ date: startOfDay, formatted: intl.formatDate(startOfDay, dateFormatOptions) });
    setIsOpen(false);

    // Return focus to the input for mouse/keyboard users, but not after a touch pick:
    // on iOS Safari, focusing the input scrolls it into view, and inside a fixed modal
    // that leaves the visual viewport offset, so later taps in the modal land above
    // where the user tapped (buttons stop responding, taps reopen this calendar).
    if (element.current && !lastInteractionWasTouch.current) {
      element.current.querySelector('input').focus();
    }

    if (onChange) {
      onChange(startOfDay);
    }
  };

  const handleOnChangeOnInput = e => {
    const inputStr = e.target.value;
    if (!inputStr) {
      setDateData({ date: null, formatted: inputStr });
      if (onChange) {
        onChange(null);
      }
      return;
    }

    if (isValidDateString(inputStr)) {
      const d = new Date(inputStr);
      if (isDayBlocked(d)) {
        setDateData({ date: dateData.date, formatted: '' });
        return;
      } else {
        const startOfDay = getStartOfDay(d);
        setDateData({ date: startOfDay, formatted: intl.formatDate(startOfDay, dateFormatOptions) });
        if (onChange) {
          onChange(startOfDay);
        }
        return;
      }
    }

    setDateData({ date: dateData.date, formatted: inputStr });
  };

  const handleBlur = () => {
    setIsOpen(false);
  };

  const handleKeyDown = e => {
    lastInteractionWasTouch.current = false;
    // Gather all escape presses to close menu
    if (e.key === 'Escape') {
      toggleOpen(false);
    }
  };
  const handleOnKeyDownOnInput = e => {
    // Gather all escape presses to close menu
    if (e.key === 'Space' || e.key === 'Enter') {
      e.preventDefault();
      toggleOpen();
    }
  };

  const toggleOpen = enforcedState => {
    if (enforcedState) {
      setIsOpen(enforcedState);
    } else {
      setIsOpen(prevState => !prevState);
    }
  };

  const disabled = props.disabled;
  const inputProps = {
    type: 'text',
    onChange: handleOnChangeOnInput,
    onKeyDown: handleOnKeyDownOnInput,
    ...(readOnly ? { readOnly } : {}),
    ...(disabled ? { disabled } : {}),
  };

  return (
    <OutsideClickHandler className={classes} onOutsideClick={handleBlur}>
      <div
        id={pickerId}
        onKeyDown={handleKeyDown}
        onPointerDown={e => {
          lastInteractionWasTouch.current = e.pointerType === 'touch';
        }}
        onTouchStart={() => {
          // Fallback for older iOS versions without Pointer Events
          lastInteractionWasTouch.current = true;
        }}
        ref={element}
      >
        <div
          className={classNames(css.inputWrapper, {
            [css.open]: isOpen,
            [inputClassName]: inputClassName,
          })}
          onClick={toggleOpen}
        >
          <input
            id={id}
            className={classNames(css.input, { [css.inputPlaceholder]: !value })}
            placeholder={placeholderText}
            value={
              props.isCustomFormat && dateData.date
                ? moment(dateData.date).format(props.customDateFormat || 'DD/MM/YYYY')
                : dateData.formatted
            }
            {...inputProps}
          />
        </div>

        {isOpen ? (
          <div className={popupClassName || css.popup}>
            <DatePicker
              range={false}
              showMonthStepper={true}
              onChange={handleChange}
              isDayBlocked={isDayBlocked}
              value={dateData.date}
              {...startDateMaybe}
              {...rest}
            />
          </div>
        ) : null}
      </div>
    </OutsideClickHandler>
  );
};

export default SingleDatePicker;
